import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import type { Embed, Media, Post } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { compact, fullDate, relative } from "../../lib/format";
import { usePrefs } from "../../lib/prefs";
import { updatePostEverywhere } from "../../lib/queryClient";
import { useNow } from "../../lib/hooks";
import { Button } from "../../components/ui/Button";
import { Tooltip } from "../../components/ui/Tooltip";
import { toast } from "../../components/ui/Toast";
import { IconCheck, IconEye, IconPlay } from "../../components/icons";
import { IconWarningCW } from "../../components/socialIcons";
import { useReducedMotion } from "../../components/ui/Avatar";
import { useAuthGate } from "../auth/AuthProvider";

/* ───────────── Polls ───────────── */

/**
 * Poll with result bars. Before voting (and while open) options are buttons;
 * multiple-choice polls collect picks and submit with "Vote". Results show
 * after voting, once closed, and always to the author.
 */
export function PollView({ post, own }: { post: Post; own: boolean }) {
  const poll = post.poll!;
  const gate = useAuthGate();
  useNow(30_000);
  const [picks, setPicks] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);
  const closed = poll.closed || new Date(poll.endsAt) <= new Date();
  const voted = poll.myVotes.length > 0;
  const showResults = (closed || voted || own) && !changing;
  const total = poll.options.reduce((n, o) => n + o.votes, 0);
  const leader = Math.max(...poll.options.map((o) => o.votes));

  const vote = async (ids: string[]) => {
    if (busy || ids.length === 0) return;
    setBusy(true);
    try {
      const updated = await api.post<Post>(`/api/posts/${post.id}/vote`, { optionIds: ids });
      updatePostEverywhere(post.id, (p) => ({ ...p, poll: updated.poll }));
      setPicks([]);
      setChanging(false);
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't record your vote"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 max-w-[560px] space-y-1.5" onClick={(e) => e.stopPropagation()} role="group" aria-label="Poll">
      {poll.options.map((o) => {
        const pct = total ? Math.round((o.votes / total) * 100) : 0;
        const mine = poll.myVotes.includes(o.id);
        if (showResults)
          return (
            <div key={o.id} className="relative flex h-9 items-center overflow-hidden rounded-md bg-surface text-sm" aria-label={`${o.text}: ${pct}%`}>
              <div
                className={cn("absolute inset-y-0 left-0 rounded-md transition-[width] duration-500", o.votes === leader && o.votes > 0 ? "bg-accent-soft" : "bg-surface-2")}
                style={{ width: `${pct}%` }}
              />
              <span className="relative flex min-w-0 flex-1 items-center gap-1.5 px-3">
                <span className={cn("truncate", o.votes === leader && o.votes > 0 ? "font-semibold text-fg" : "text-fg-2")}>{o.text}</span>
                {mine && <IconCheck className="icon size-4 shrink-0 text-accent" aria-label="Your vote" />}
              </span>
              <span className="relative px-3 font-medium text-fg-2 tabular">{pct}%</span>
            </div>
          );
        const picked = picks.includes(o.id);
        return (
          <button
            key={o.id}
            type="button"
            disabled={busy}
            aria-pressed={poll.multiple ? picked : undefined}
            onClick={gate(() => (poll.multiple ? setPicks((x) => (x.includes(o.id) ? x.filter((y) => y !== o.id) : [...x, o.id])) : void vote([o.id])))}
            className={cn(
              "flex h-9 w-full items-center gap-2 rounded-md border px-3 text-left text-sm font-medium transition-colors",
              picked ? "border-accent bg-accent-soft text-fg" : "border-line-strong text-fg hover:bg-surface",
            )}
          >
            {poll.multiple && (
              <span className={cn("flex size-4 shrink-0 items-center justify-center rounded border", picked ? "border-accent bg-accent text-accent-fg" : "border-line-strong")} aria-hidden>
                {picked && <IconCheck className="size-3" />}
              </span>
            )}
            <span className="truncate">{o.text}</span>
          </button>
        );
      })}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-0.5 text-xs text-fg-3">
        <span className="tabular">
          {compact(poll.totalVoters)} {poll.totalVoters === 1 ? "vote" : "votes"}
        </span>
        <span aria-hidden>·</span>
        <Tooltip label={fullDate(poll.endsAt)}>
          <span>{closed ? "Final results" : `Ends ${relative(poll.endsAt)}`}</span>
        </Tooltip>
        {poll.multiple && !closed && <span>· Choose any</span>}
        {!closed && voted && !changing && (
          <button type="button" onClick={() => setChanging(true)} className="ml-auto font-medium text-fg-2 hover:text-fg">
            Change vote
          </button>
        )}
        {!closed && own && !voted && !changing && (
          <button type="button" onClick={() => setChanging(true)} className="ml-auto font-medium text-fg-2 hover:text-fg">
            Vote
          </button>
        )}
        {!showResults && poll.multiple && (
          <Button size="sm" variant="primary" className="ml-auto" disabled={!picks.length} loading={busy} onClick={gate(() => void vote(picks))}>
            Vote
          </Button>
        )}
        {changing && !poll.multiple && (
          <button type="button" onClick={() => setChanging(false)} className="ml-auto font-medium text-fg-2 hover:text-fg">
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

/* ───────────── Content warnings & sensitive media ───────────── */

/**
 * A content warning hides the whole post body until revealed. Sensitive media
 * without a warning follows the viewer's preference: blur (tap to show),
 * show, or hide (collapsed to a line with "Show").
 */
export function ContentWarningGate({ warning, children, revealed, onReveal }: { warning: string; children: ReactNode; revealed: boolean; onReveal: (v: boolean) => void }) {
  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 rounded-md bg-surface px-3 py-2 text-sm text-fg-2" onClick={(e) => e.stopPropagation()}>
        <IconWarningCW className="icon size-[18px] shrink-0 text-warning" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{warning}</span>
        <button type="button" onClick={() => onReveal(!revealed)} className="shrink-0 rounded px-1.5 py-0.5 text-sm font-medium text-fg hover:bg-surface-2" aria-expanded={revealed}>
          {revealed ? "Hide" : "Show"}
        </button>
      </div>
      {revealed && children}
    </div>
  );
}

export function SensitiveMedia({ children, sensitive }: { children: ReactNode; sensitive: boolean }) {
  const { sensitiveMedia } = usePrefs();
  const [shown, setShown] = useState(false);
  if (!sensitive || sensitiveMedia === "show" || shown) return <>{children}</>;
  if (sensitiveMedia === "hide")
    return (
      <div className="mt-3 flex items-center gap-2 rounded-md bg-surface px-3 py-2 text-sm text-fg-3" onClick={(e) => e.stopPropagation()}>
        <IconEye className="icon size-[18px]" aria-hidden />
        <span className="flex-1">Sensitive media hidden</span>
        <button type="button" onClick={() => setShown(true)} className="rounded px-1.5 py-0.5 font-medium text-fg hover:bg-surface-2">
          Show
        </button>
      </div>
    );
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <div className="pointer-events-none select-none blur-2xl saturate-50" aria-hidden>
        {children}
      </div>
      <button type="button" onClick={() => setShown(true)} className="absolute inset-0 mt-3 flex flex-col items-center justify-center gap-1 rounded-lg bg-overlay text-sm font-medium text-white">
        <IconEye className="icon size-6" aria-hidden />
        Sensitive media · Show
      </button>
    </div>
  );
}

/* ───────────── Embeds: GIFs and link previews ───────────── */

function GifEmbed({ e }: { e: Embed }) {
  const prefs = usePrefs();
  const reduced = useReducedMotion();
  const auto = prefs.gifAutoplay && !reduced;
  const [play, setPlay] = useState(auto);
  const still = e.thumbnail?.url;
  const src = play || !still ? (e.image?.url ?? e.url) : still;
  if (!src) return null;
  return (
    <div className="relative mt-3 w-fit max-w-full overflow-hidden rounded-lg border border-line" onClick={(ev) => ev.stopPropagation()}>
      <img src={src} alt={e.title ?? "GIF"} loading="lazy" width={e.image?.width} height={e.image?.height} className="block h-auto max-h-[420px] w-auto max-w-full bg-surface-2" />
      {!play && still && (
        <button type="button" onClick={() => setPlay(true)} aria-label="Play GIF" className="absolute inset-0 flex items-center justify-center bg-black/25 text-white">
          <IconPlay className="icon size-8" aria-hidden />
        </button>
      )}
      <span className="absolute bottom-1.5 left-1.5 rounded bg-black/65 px-1 text-[11px] font-semibold text-white">GIF</span>
    </div>
  );
}

function LinkEmbed({ e }: { e: Embed }) {
  if (!e.url) return null;
  const host = (() => {
    try {
      return new URL(e.url).hostname.replace(/^www\./, "");
    } catch {
      return "";
    }
  })();
  return (
    <a
      href={e.url}
      target="_blank"
      rel="noopener noreferrer nofollow ugc"
      onClick={(ev) => ev.stopPropagation()}
      className="mt-3 flex max-w-[560px] overflow-hidden rounded-lg border border-line transition-colors hover:border-line-strong"
    >
      {e.thumbnail?.url || e.image?.url ? <img src={e.thumbnail?.url ?? e.image?.url} alt="" loading="lazy" className="size-20 shrink-0 bg-surface-2 object-cover" /> : null}
      <span className="min-w-0 flex-1 px-3 py-2">
        <span className="block truncate text-xs text-fg-3">{e.provider?.name ?? host}</span>
        {e.title && <span className="block truncate text-sm font-medium text-fg">{e.title}</span>}
        {e.description && <span className="line-clamp-2 text-sm text-fg-2">{e.description}</span>}
      </span>
    </a>
  );
}

export function PostEmbeds({ embeds }: { embeds: Embed[] }) {
  const { linkPreviews } = usePrefs();
  if (!embeds.length) return null;
  return (
    <>
      {embeds.map((e, i) => (e.type === "gif" ? <GifEmbed key={i} e={e} /> : e.type === "link" && linkPreviews ? <LinkEmbed key={i} e={e} /> : null))}
    </>
  );
}

/* ───────────── People tagged in images ───────────── */

/** "With Ann and Bob": people tagged across a post's images, linking to their profiles. */
export function TaggedPeople({ media }: { media: Media[] }) {
  const people = [...new Map(media.flatMap((m) => m.tags ?? []).map((t) => [t.user.id, t.user])).values()];
  if (!people.length) return null;
  return (
    <div className="mt-1.5 text-xs text-fg-3" onClick={(e) => e.stopPropagation()}>
      With{" "}
      {people.map((u, i) => (
        <span key={u.id}>
          {i > 0 && (i === people.length - 1 ? " and " : ", ")}
          <Link to={`/u/${u.username}`} className="font-medium text-fg-2 hover:underline">
            {u.displayName}
          </Link>
        </span>
      ))}
    </div>
  );
}
